import React from 'react';
import type { CSSProperties } from 'react';

import styles from './EmployeeAvatar.module.css';

type EmployeeAvatarProps = {
  name?: string;
  imageUrl?: string | null;
  colorPreference?: string;
  className?: string;
  size?: number;
  isUploading?: boolean;
  showSuccess?: boolean;
};

const resolveEmployeeColor = (colorPreference?: string): string => {
  const fallback = '#5b7cfa';
  const value = (colorPreference || '').trim();
  if (!value) {
    return fallback;
  }

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', value) ? value : fallback;
  }

  return value;
};

const getInitials = (name?: string) => {
  const parts = (name || '')
    .split(' ')
    .map(part => part.trim())
    .filter(Boolean);

  const initials = parts
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('');

  return initials || 'U';
};

export const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({
  name,
  imageUrl,
  colorPreference,
  className,
  size = 44,
  isUploading = false,
  showSuccess = false,
}) => {
  const accent = resolveEmployeeColor(colorPreference);
  const style = {
    width: size,
    height: size,
    ['--employee-avatar-size' as string]: `${size}px`,
    ['--employee-avatar-accent' as string]: accent,
    ['--employee-avatar-glow' as string]: `${accent}33`,
  } as CSSProperties;

  return (
    <span
      className={[
        styles.avatar,
        isUploading ? styles.avatarUploading : '',
        showSuccess ? styles.avatarSuccess : '',
        className,
      ].filter(Boolean).join(' ')}
      style={style}
      aria-label={name || 'Employee avatar'}
    >
      {imageUrl ? (
        <img className={styles.image} src={imageUrl} alt={name || 'Employee avatar'} />
      ) : (
        <span className={styles.initials}>{getInitials(name)}</span>
      )}

      {isUploading ? (
        <span className={styles.overlay} aria-hidden="true">
          <span className={styles.spinner} />
        </span>
      ) : null}

      {!isUploading && showSuccess ? (
        <span className={styles.overlay} aria-hidden="true">
          <span className={styles.successBadge}>✓</span>
        </span>
      ) : null}
    </span>
  );
};

export default EmployeeAvatar;